import React from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { ArrowDownIcon } from 'lucide-react';
import { Logo } from './snakker/Logo';
const paragraphs = ["The objective is to bring Snakker's snackification methodology to AbbVie Pro - the HCP portal and the therapeutic-area websites it links to - without restructuring either.", 'The principle is simple: users invest effort only when the expected reward is clear.', 'Instead of opening with every available pathway at once, the experience begins with a concise, branded introduction. It establishes value before asking the user to choose what they need.', 'As intent becomes clearer, the interface introduces focused interactions and progressively reveals clinical profile, safety, dosing, resources and events. Complexity increases only when the user is ready for it.'];
export function IntroScreen() {
  const reduceMotion = useReducedMotion();
  return <section id="top" className="flex min-h-screen w-full items-center bg-presentation-canvas px-6 py-20" aria-labelledby="intro-title" data-id="element-243" data-mp="21flanhayrv2l">
      <div className="mx-auto grid w-full max-w-6xl gap-12 lg:grid-cols-[0.72fr_1.28fr] lg:items-center" data-id="element-244" data-mp="2ox0a3f0fgwbo">
        <motion.div initial={reduceMotion ? false : {
        opacity: 0,
        y: 16
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.28,
        ease: [0.23, 1, 0.32, 1]
      }} data-id="element-245" data-mp="2ysuykqrc1lmd">
          <div className="mb-12 flex items-center gap-4" data-id="element-246" data-mp="37sqcv4g6pwqm">
            <Logo width={36} height={36} alt="SNAKKER" data-id="element-247" data-mp="3cscqvg3u7h0h" />
            <div className="h-8 w-px bg-slate-300" aria-hidden="true" data-id="element-248" data-mp="3cr1jl6b6nr4m" />
            <div className="flex items-center gap-2 text-abbvie-navy" data-id="element-249" data-mp="3hkdxomi8kfj4">
              <span className="text-xl font-semibold tracking-tight" data-id="element-250" data-mp="3mi60ibkf5m23">abbvie</span>
              <span className="rounded bg-abbvie-pro px-2 py-0.5 text-[10px] font-bold text-white" data-id="element-251" data-mp="3mk8sh154tp9g">pro</span>
            </div>
          </div>
          <p className="mb-3 text-sm font-semibold text-primary" data-id="element-252" data-mp="3vk5jvaiwp287">Engagement strategy</p>
          <h1 id="intro-title" className="max-w-lg text-4xl font-bold leading-tight text-secondary md:text-6xl" data-id="element-253" data-mp="1vgcojhvgjygm">
            Snackifying AbbVie Pro
          </h1>
          <div className="mt-8 h-1 w-20 bg-abbvie-cyan" data-id="element-254" data-mp="1rdg2tiamfm8g" />
          <div className="mt-6 max-w-lg rounded-xl border border-sky-200 bg-white p-4 shadow-sm" data-id="element-255" data-mp="257ftiuricxl6">
            <p className="text-xs font-bold text-abbvie-cyan" data-id="element-256" data-mp="20f5verg8or7t">SKYRIZI WORKED EXAMPLE</p>
            <p className="mt-1 text-sm font-medium leading-relaxed text-slate-600" data-id="element-257" data-mp="2f3skhkvo4xc8">
              SKYRIZI demonstrates one way to Snakify AbbVie Pro. The same approach can be applied across all AbbVie brands.
            </p>
          </div>
        </motion.div>

        <div className="border-l border-slate-300 pl-6 md:pl-10" data-id="element-258" data-mp="2j6fgzz3ab6fr">
          {paragraphs.map((paragraph, index) => <motion.p key={paragraph} initial={reduceMotion ? false : {
          opacity: 0,
          y: 10
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.24,
          delay: Math.min(index * 0.04, 0.12),
          ease: [0.23, 1, 0.32, 1]
        }} className="mb-5 text-base leading-relaxed text-slate-600 md:text-lg" data-id="element-259" data-mp="tqz7gilg7ipc" data-mpi={index}>
              {paragraph}
            </motion.p>)}
          <p className="mb-5 text-sm italic leading-relaxed text-slate-400" data-id="element-260" data-mp="2vp1cgauu886c">
            SKYRIZI is illustrative rather than exclusive: this AbbVie Pro Snakification model is designed to scale across all AbbVie brands.
          </p>
          <div className="mt-10 flex items-center gap-2 text-sm font-semibold text-primary" data-id="element-261" data-mp="2momof3qhoez5">
            <ArrowDownIcon className="h-4 w-4" aria-hidden="true" data-id="element-262" data-mp="28pd9e7e0rqwr" />
            Scroll to explore the approach
          </div>
        </div>
      </div>
    </section>;
}